import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Briefcase, ClipboardCheck, GraduationCap, UserCheck, Building2, ClipboardList, Calendar, Anchor, ArrowUpRight, MessageSquare, FileText, ClipboardPen, ChevronDown } from 'lucide-react';
import { RecapFormateurs } from './RecapFormateurs';
import { RecapTuteurs } from './RecapTuteurs';
import { dateFr } from './RecapCommun';
import { TuileAdministratif } from './TuileAdministratif';
import { TuileCommunication } from './TuileCommunication';

const PETITES = [
  { to: '/gestion-formateurs', label: 'Formateurs', Icone: GraduationCap, id: 'formateurs', cle: 'formateurs' },
  { to: '/liste-tuteurs', label: 'Tuteurs', Icone: UserCheck, id: 'tuteurs', cle: 'tuteurs' },
  { to: '/liste-entreprises', label: 'Entreprises', Icone: Building2, id: 'entreprises', cle: 'entreprises' },
  { to: '/presence-centre', label: 'Présences', Icone: ClipboardList, id: 'presences' },
  { to: '/planning', label: 'Planning', Icone: Calendar, id: 'planning' },
  { to: '/moniteurs', label: 'Moniteurs VNM', Icone: Anchor, id: 'moniteurs-vnm' },
];

// Grande tuile : lien (to) ou bascule d'un récap (onClick)
const GrandeTuile = ({ to, onClick, actif, Icone, titre, valeur, sousTitre, ton, testId, children }) => {
  const classes = `group relative overflow-hidden rounded-2xl p-5 sm:p-6 text-left text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5 ${ton} ${actif ? 'ring-4 ring-offset-2 ring-slate-300' : ''}`;
  const contenu = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-xl bg-white/15 p-2.5"><Icone className="h-6 w-6" /></div>
        {onClick
          ? <ChevronDown className={`h-5 w-5 opacity-70 transition-transform duration-200 ${actif ? 'rotate-180' : ''}`} />
          : <ArrowUpRight className="h-5 w-5 opacity-60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
      </div>
      <p className="mt-4 text-sm font-medium text-white/80">{titre}</p>
      <p className="text-3xl sm:text-4xl font-display font-bold leading-tight" data-testid={`${testId}-valeur`}>{valeur}</p>
      <p className="text-sm text-white/80">{sousTitre}</p>
      {children}
    </>
  );
  return onClick
    ? <button type="button" onClick={onClick} aria-expanded={actif} className={classes} data-testid={testId}>{contenu}</button>
    : <Link to={to} className={classes} data-testid={testId}>{contenu}</Link>;
};

const Puce = ({ children }) => <span className="rounded-full bg-white/20 px-2.5 py-1 text-xs">{children}</span>;

// Accès rapide : Business, Administratif CFA, Candidats, Stagiaires, Rapports, Évaluations en grand ; le reste en petites tuiles
export const AccesRapide = ({ stats, parCentre }) => {
  const [business, setBusiness] = useState(null);
  const [nonLus, setNonLus] = useState(0);
  const [candidats, setCandidats] = useState(null);
  const [recaps, setRecaps] = useState({});
  const [recapOuvert, setRecapOuvert] = useState(null);
  const basculer = (id) => setRecapOuvert(o => (o === id ? null : id));

  useEffect(() => {
    Promise.allSettled([axios.get('/dashboard/recap-formateurs', { params: { jours: 7 } }), axios.get('/dashboard/recap-tuteurs', { params: { jours: 7 } })])
      .then(([f, t]) => setRecaps({ f: f.value?.data, t: t.value?.data }));
    axios.get('/candidats/stats-tuile').then(({ data }) => setCandidats(data)).catch(() => setCandidats({ en_cours: 0, documents_a_valider: 0, dossiers_complets: 0, tests_a_envoyer: 0 }));
    axios.get('/notifications/unread-count').then(({ data }) => setNonLus(data?.count || 0)).catch(() => {});
    axios.get('/crm/prospects/stats').then(({ data }) => {
      const s = data.by_status || {};
      setBusiness({ enCours: (data.total || 0) - (s.client || 0) - (s.perdu || 0), clients: s.client || 0, financement: data.recherche_financement || 0 });
    }).catch(() => setBusiness({ enCours: 0, clients: 0, financement: 0 }));
  }, []);

  return (
    <section className="space-y-3" data-testid="acces-rapide">
      <h2 className="text-base md:text-lg font-display font-semibold text-[#0F172A]">Accès rapide</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        <GrandeTuile to="/crm" Icone={Briefcase} titre="Business en cours" valeur={business ? business.enCours : '…'} sousTitre="prospects en cours" ton="bg-[#8DC63F]" testId="tuile-business">
          {business && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-white/20 px-2.5 py-1 text-xs"><strong>{business.clients}</strong> client{business.clients > 1 ? 's' : ''}</span>
              {business.financement > 0 && <span className="rounded-full bg-white/20 px-2.5 py-1 text-xs"><strong>{business.financement}</strong> en recherche de financement</span>}
            </div>
          )}
        </GrandeTuile>
        <TuileAdministratif absences={stats.demandesAbsenceEnAttente} />
        <TuileCommunication />
        <GrandeTuile to="/gestion-candidats" Icone={ClipboardCheck} titre="Candidats" valeur={candidats ? candidats.en_cours : '…'} sousTitre="candidats en cours" ton="bg-teal-600" testId="tuile-candidats">
          {candidats && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Puce><strong data-testid="tuile-candidats-docs">{candidats.documents_a_valider}</strong> document{candidats.documents_a_valider > 1 ? 's' : ''} à valider</Puce>
              <Puce><strong data-testid="tuile-candidats-complets">{candidats.dossiers_complets}</strong> dossier{candidats.dossiers_complets > 1 ? 's' : ''} complet{candidats.dossiers_complets > 1 ? 's' : ''}</Puce>
              <Puce><strong data-testid="tuile-candidats-tests">{candidats.tests_a_envoyer}</strong> test{candidats.tests_a_envoyer > 1 ? 's' : ''} à envoyer</Puce>
            </div>
          )}
        </GrandeTuile>
        <GrandeTuile to="/liste-stagiaires" Icone={Users} titre="Stagiaires" valeur={stats.stagiaires} sousTitre="en formation" ton="bg-[#0F172A]" testId="tuile-stagiaires">
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(parCentre).map(([centre, n]) => (
              <span key={centre} className="rounded-full bg-white/10 px-2.5 py-1 text-xs">{centre} · <strong>{n}</strong></span>
            ))}
          </div>
        </GrandeTuile>
        <GrandeTuile onClick={() => basculer('formateurs')} actif={recapOuvert === 'formateurs'} Icone={FileText} titre="Rapports formateurs"
          valeur={recaps.f ? recaps.f.nb_rapports : '…'} sousTitre="rapports journaliers (7 jours)" ton="bg-blue-600" testId="tuile-rapports-formateurs">
          {recaps.f && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Puce><strong>{recaps.f.nb_notes}</strong> note{recaps.f.nb_notes > 1 ? 's' : ''} saisie{recaps.f.nb_notes > 1 ? 's' : ''}</Puce>
              {recaps.f.dernier_rapport && <Puce>Dernier : {recaps.f.dernier_rapport.nom}, {dateFr(recaps.f.dernier_rapport.date)}</Puce>}
            </div>
          )}
        </GrandeTuile>
        <GrandeTuile onClick={() => basculer('tuteurs')} actif={recapOuvert === 'tuteurs'} Icone={ClipboardPen} titre="Évaluations tuteurs"
          valeur={recaps.t ? recaps.t.nb_evaluations : '…'} sousTitre="évaluations en entreprise (7 jours)" ton="bg-orange-500" testId="tuile-evaluations-tuteurs">
          {recaps.t && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Puce><strong>{recaps.t.heures}</strong> h d'encadrement</Puce>
              <Puce><strong>{recaps.t.nb_competent}</strong> compétent</Puce>
            </div>
          )}
        </GrandeTuile>
      </div>
      {recapOuvert && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-200" data-testid={`recap-panneau-${recapOuvert}`}>
          {recapOuvert === 'formateurs' ? <RecapFormateurs /> : <RecapTuteurs />}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
        {PETITES.map(({ to, label, Icone, id, cle }) => (
          <Link key={id} to={to} data-testid={`tuile-${id}`}
            className="group flex flex-col justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 transition-colors duration-200 hover:border-[#8DC63F]">
            <Icone className="h-5 w-5 text-slate-500 transition-colors group-hover:text-[#8DC63F]" />
            <div>
              {cle && <p className="text-xl font-display font-bold text-[#0F172A] leading-none">{stats[cle]}</p>}
              <p className="text-xs sm:text-sm text-slate-600 mt-1">{label}</p>
            </div>
          </Link>
        ))}
        <Link to="/notifications" data-testid="tuile-messagerie"
          className={`relative flex flex-col justify-between gap-3 rounded-xl border p-3 sm:p-4 transition-colors duration-200 ${nonLus > 0 ? 'border-red-200 bg-red-50 hover:border-red-400' : 'border-slate-200 bg-white hover:border-[#8DC63F]'}`}>
          <MessageSquare className={`h-5 w-5 ${nonLus > 0 ? 'text-red-500' : 'text-slate-500'}`} />
          {nonLus > 0 && (
            <span className="absolute top-2 right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="tuile-messagerie-badge">
              {nonLus > 99 ? '99+' : nonLus}
            </span>
          )}
          <div>
            <p className={`text-xs sm:text-sm mt-1 ${nonLus > 0 ? 'text-red-600 font-medium' : 'text-slate-600'}`}>Messagerie</p>
          </div>
        </Link>
      </div>
    </section>
  );
};
